import type { UppFloorPlanElement } from '../plan/floor-plan-element';
import { floorPlanElementBBoxVisualCenterCm } from './floor-plan-multi-table-uniform-sync';
import { cmPointToStagePx, stagePxToCmPoint, type UppFloorPlanViewTransform } from './floor-plan-view-transform';
import { roundFloorPlanGeometryCm } from './floor-plan-konva-sync';

/** AABB intersection in the same space (e.g. `Stage` coordinates). */
export function floorPlanStageRectsIntersect(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
): boolean {
  return !(a.x + a.width < b.x || b.x + b.width < a.x || a.y + a.height < b.y || b.y + b.height < a.y);
}

/** Axis-aligned bbox center in model space (cm); does not project element rotation. */
export function floorPlanElementAxisCenterCm(el: UppFloorPlanElement): { cx: number; cy: number } {
  return {
    cx: el.xCm + el.widthCm / 2,
    cy: el.yCm + el.heightCm / 2,
  };
}

/** Geometric center of logical rectangle (same as {@link floorPlanElementAxisCenterCm}). */
export function floorPlanElementGeometricCenterCm(el: UppFloorPlanElement): { cx: number; cy: number } {
  return floorPlanElementAxisCenterCm(el);
}

/** Four corners of the bbox after applying {@link UppFloorPlanElement.rotationDeg} around the center. */
export function floorPlanElementRotatedCornersCm(el: UppFloorPlanElement): Array<{ x: number; y: number }> {
  const w = el.widthCm;
  const h = el.heightCm;
  const { cx, cy } = floorPlanElementGeometricCenterCm(el);
  const rad = (el.rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const corners = [
    { dx: -w / 2, dy: -h / 2 },
    { dx: w / 2, dy: -h / 2 },
    { dx: w / 2, dy: h / 2 },
    { dx: -w / 2, dy: h / 2 },
  ];
  return corners.map(({ dx, dy }) => {
    const rx = dx * cos - dy * sin;
    const ry = dx * sin + dy * cos;
    return { x: cx + rx, y: cy + ry };
  });
}

/**
 * Element AABB in **Stage px** (same convention as `cmPointToStagePx`: already includes 0/90-degree canvas orientation).
 */
export function floorPlanElementScreenBoundsPx(
  t: UppFloorPlanViewTransform,
  el: UppFloorPlanElement,
): { minSx: number; maxSx: number; minSy: number; maxSy: number } {
  const pts = floorPlanElementRotatedCornersCm(el);
  let minSx = Infinity;
  let maxSx = -Infinity;
  let minSy = Infinity;
  let maxSy = -Infinity;
  for (const p of pts) {
    const s = cmPointToStagePx(t, p.x, p.y);
    minSx = Math.min(minSx, s.x);
    maxSx = Math.max(maxSx, s.x);
    minSy = Math.min(minSy, s.y);
    maxSy = Math.max(maxSy, s.y);
  }
  return { minSx, maxSx, minSy, maxSy };
}

/** Center of logical box on screen (Stage): same criterion as Konva / {@link floorPlanElementBBoxVisualCenterCm}. */
function elementBBoxVisualCenterStagePx(t: UppFloorPlanViewTransform, el: UppFloorPlanElement): { sx: number; sy: number } {
  const { xCm: cx, yCm: cy } = floorPlanElementBBoxVisualCenterCm(el);
  const p = cmPointToStagePx(t, cx, cy);
  return { sx: p.x, sy: p.y };
}

function elementScreenCenterFromBoundsPx(bounds: { minSx: number; maxSx: number; minSy: number; maxSy: number }): { sx: number; sy: number } {
  return {
    sx: (bounds.minSx + bounds.maxSx) / 2,
    sy: (bounds.minSy + bounds.maxSy) / 2,
  };
}

/**
 * Moves the element so the visual bbox center (same rule as {@link floorPlanElementBBoxVisualCenterCm})
 * matches the Stage target while preserving width/height/rotation.
 */
function moveElementCenterToStagePx(
  t: UppFloorPlanViewTransform,
  el: UppFloorPlanElement,
  target: { sx: number; sy: number },
): void {
  const current = elementBBoxVisualCenterStagePx(t, el);
  const currentCm = stagePxToCmPoint(t, current.sx, current.sy);
  const targetCm = stagePxToCmPoint(t, target.sx, target.sy);
  el.xCm = roundFloorPlanGeometryCm(el.xCm + (targetCm.xCm - currentCm.xCm));
  el.yCm = roundFloorPlanGeometryCm(el.yCm + (targetCm.yCm - currentCm.yCm));
}

/**
 * **Align vertically (screen):** same **Stage horizontal** coordinate (`sx`) for each element center;
 * keeps each element `sy`. Equivalent to aligning on a user-visible vertical line (independent of 0/90 plan rotation).
 */
export function alignFloorPlanElementsCentersOnX(
  t: UppFloorPlanViewTransform,
  elements: readonly UppFloorPlanElement[],
): void {
  if (elements.length < 2) {
    return;
  }
  const stages = elements.map((el) => elementBBoxVisualCenterStagePx(t, el));
  const targetSx = stages.reduce((acc, s) => acc + s.sx, 0) / elements.length;
  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    const stage = stages[i];
    if (!el || !stage) {
      continue;
    }
    moveElementCenterToStagePx(t, el, { sx: targetSx, sy: stage.sy });
  }
}

/**
 * **Align horizontally (screen):** same Stage `sy` for centers; keeps each `sx`.
 */
export function alignFloorPlanElementsCentersOnY(
  t: UppFloorPlanViewTransform,
  elements: readonly UppFloorPlanElement[],
): void {
  if (elements.length < 2) {
    return;
  }
  const stages = elements.map((el) => elementBBoxVisualCenterStagePx(t, el));
  const targetSy = stages.reduce((acc, s) => acc + s.sy, 0) / elements.length;
  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    const stage = stages[i];
    if (!el || !stage) {
      continue;
    }
    moveElementCenterToStagePx(t, el, { sx: stage.sx, sy: targetSy });
  }
}

/**
 * **Distribute vertically (screen):** equal spacing between projected **bottom/top** edges in Stage (`sy`).
 * Preserves total span (from first `minSy` to last `maxSy` in vertical screen reading order).
 */
export function distributeFloorPlanElementsEvenGapsAlongY(
  t: UppFloorPlanViewTransform,
  elements: readonly UppFloorPlanElement[],
): void {
  if (elements.length < 2) {
    return;
  }
  const sorted = [...elements].sort((a, b) => {
    const ba = floorPlanElementScreenBoundsPx(t, a);
    const bb = floorPlanElementScreenBoundsPx(t, b);
    if (ba.minSy !== bb.minSy) {
      return ba.minSy - bb.minSy;
    }
    return a.uuid.localeCompare(b.uuid);
  });
  const bounds = sorted.map((el) => floorPlanElementScreenBoundsPx(t, el));
  const globalMinTop = Math.min(...bounds.map((b) => b.minSy));
  const globalMaxBottom = Math.max(...bounds.map((b) => b.maxSy));
  const span = globalMaxBottom - globalMinTop;
  let totalH = 0;
  for (const b of bounds) {
    totalH += b.maxSy - b.minSy;
  }
  const n = sorted.length;
  const gap = (span - totalH) / (n - 1);
  if (!Number.isFinite(gap)) {
    return;
  }
  const targets: number[] = [];
  let cursor = globalMinTop;
  for (let i = 0; i < n; i++) {
    targets.push(cursor);
    const b = bounds[i];
    if (!b) {
      continue;
    }
    cursor += b.maxSy - b.minSy + gap;
  }
  for (let i = 0; i < n; i++) {
    const el = sorted[i];
    const b = bounds[i];
    const target = targets[i];
    if (!el || !b || target === undefined) {
      continue;
    }
    const deltaSy = target - b.minSy;
    const center = elementScreenCenterFromBoundsPx(b);
    moveElementCenterToStagePx(t, el, { sx: center.sx, sy: center.sy + deltaSy });
  }
}

/**
 * **Distribute horizontally (screen):** equal spacing between left/right edges in Stage (`sx`).
 */
export function distributeFloorPlanElementsEvenGapsAlongX(
  t: UppFloorPlanViewTransform,
  elements: readonly UppFloorPlanElement[],
): void {
  if (elements.length < 2) {
    return;
  }
  const sorted = [...elements].sort((a, b) => {
    const ba = floorPlanElementScreenBoundsPx(t, a);
    const bb = floorPlanElementScreenBoundsPx(t, b);
    if (ba.minSx !== bb.minSx) {
      return ba.minSx - bb.minSx;
    }
    return a.uuid.localeCompare(b.uuid);
  });
  const bounds = sorted.map((el) => floorPlanElementScreenBoundsPx(t, el));
  const globalMinLeft = Math.min(...bounds.map((b) => b.minSx));
  const globalMaxRight = Math.max(...bounds.map((b) => b.maxSx));
  const span = globalMaxRight - globalMinLeft;
  let totalW = 0;
  for (const b of bounds) {
    totalW += b.maxSx - b.minSx;
  }
  const n = sorted.length;
  const gap = (span - totalW) / (n - 1);
  if (!Number.isFinite(gap)) {
    return;
  }
  const targets: number[] = [];
  let cursor = globalMinLeft;
  for (let i = 0; i < n; i++) {
    targets.push(cursor);
    const b = bounds[i];
    if (!b) {
      continue;
    }
    cursor += b.maxSx - b.minSx + gap;
  }
  for (let i = 0; i < n; i++) {
    const el = sorted[i];
    const b = bounds[i];
    const target = targets[i];
    if (!el || !b || target === undefined) {
      continue;
    }
    const deltaSx = target - b.minSx;
    const center = elementScreenCenterFromBoundsPx(b);
    moveElementCenterToStagePx(t, el, { sx: center.sx + deltaSx, sy: center.sy });
  }
}
